import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import EventLayout from "@/components/event/EventLayout";
import { eventApi } from "@/lib/eventHub";
import { Heart, MessageSquare, Camera, ArrowUpRight, Users, Rocket, Award, Star, Loader2 } from "lucide-react";

/**
 * Post-Event home (activates automatically Sun 2026-07-19 09:00 ET, or via admin override).
 * Priorities per spec §6 MODE 3: survey · photos · donate · 5K · AI Build Lab · 2027 list ·
 * volunteer · sponsors · social · highlights.
 */
export default function PostEventHome() {
  const [config, setConfig] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let m = true;
    (async () => {
      try {
        const c = await eventApi.config();
        if (m) setConfig(c.data);
      } catch { /* noop */ }
      // Attempt to pull survey URL from public settings via /event/config action_cards
      setLoading(false);
      if (m) setSettings(null);
    })();
    return () => { m = false; };
  }, []);
  void settings;

  const social = config?.social || {};
  const photos = config?.photos || {};
  const donation = config?.donation || {};

  // Survey URL is only in admin settings; expose via a lightweight direct fetch of /event/admin/site-mode? No — it's admin-only.
  // Best UX: read from action_cards first, then fall back to blank.
  const cards = config?.action_cards || [];
  const surveyCard = cards.find((c) => (c.id || "").includes("survey"));
  const surveyUrl = surveyCard?.url || "";

  if (loading) {
    return (
      <EventLayout showProgramBanner={false}>
        <div className="p-16 text-center text-emerald-800/50"><Loader2 className="mx-auto animate-spin" /></div>
      </EventLayout>
    );
  }

  return (
    <EventLayout showProgramBanner={false} className="bg-gradient-to-b from-emerald-950 via-emerald-900 to-emerald-950">
      <section className="relative overflow-hidden text-white">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-fuchsia-600 opacity-25 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-emerald-500 opacity-20 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-lg px-5 pt-8 pb-6">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-fuchsia-500/20 border border-fuchsia-300/30 px-3 py-1 mb-4">
            <Star size={12} className="text-fuchsia-200" />
            <span className="text-[10px] tracking-[0.24em] uppercase font-[Outfit] font-bold text-fuchsia-100">Post-Event</span>
          </div>
          <h1 className="font-[Cormorant_Garamond] text-4xl sm:text-5xl leading-[1.02] text-white">
            Thank you for joining the <span className="italic text-fuchsia-300">2026 Business Showers</span>
          </h1>
          <p className="mt-3 font-[Outfit] text-sm text-white/70 leading-relaxed">
            Two nights. Twelve honorees. One unforgettable weekend. Help us capture, celebrate,
            and carry the momentum forward.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-lg px-5 py-6 space-y-3 bg-white text-emerald-950 rounded-t-3xl -mt-4 relative">
        {/* Survey — top priority per spec */}
        {surveyUrl && (
          <a
            href={surveyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="post-survey"
            className="block rounded-2xl bg-fuchsia-500 text-white px-5 py-4 shadow-lg shadow-fuchsia-500/30"
          >
            <div className="flex items-start gap-3">
              <span className="h-11 w-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0"><MessageSquare size={20} /></span>
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-white/80">2 minutes · Community pulse</div>
                <div className="font-[Cormorant_Garamond] text-2xl leading-tight">Share Your Business Showers Experience</div>
                <div className="mt-1 text-xs font-[Outfit] text-white/80">Your voice shapes the 2027 experience.</div>
              </div>
              <ArrowUpRight size={18} className="mt-1 shrink-0" />
            </div>
          </a>
        )}

        <ActionRow testid="post-upload" icon={<Camera size={18} />} label="Upload your photos and videos" href={photos.upload_url} />
        <ActionRow testid="post-gallery" icon={<Camera size={18} />} label="View the event photo gallery" href={photos.gallery_url} />

        {/* Donation quick-pick chips · one-tap suggested amounts deep-link to
            PayPal with the amount pre-selected (hosted-button URL supports
            &amount=X). Falls back to a plain Support button below for the
            "let me choose an amount" path. Higher conversion than a single
            donate button. */}
        {donation.url && (
          <DonationQuickPick
            baseUrl={donation.url}
            amounts={donation.amounts || [25, 50, 100, 250, 500]}
            impact={donation.impact || ""}
          />
        )}
        <ActionRow testid="post-donate" icon={<Heart size={18} />} label="Choose a custom amount" href={donation.url} highlight />
        {cards.filter((c) => c.active
          && !c.id?.includes("survey")
          // Hide any duplicate "Support the Mission" action card since we now
          // have a dedicated donation quick-pick section above.
          && !(c.title || "").toLowerCase().includes("support the mission")
          && !(c.id || "").toLowerCase().includes("donate")
        ).sort((a, b) => (a.order || 0) - (b.order || 0)).map((c) => (
          <ActionRow key={c.id} testid={`post-card-${c.id}`} icon={iconFor(c.id)} label={c.title || c.cta || c.id} hint={c.description} href={c.url} />
        ))}

        {/* Social + highlights */}
        {(social.facebook || social.instagram || social.linkedin_org) && (
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4">
            <div className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-fuchsia-700 mb-2">Follow Freedom In Me</div>
            <div className="flex flex-wrap gap-2">
              {social.facebook && <a href={social.facebook} target="_blank" rel="noopener noreferrer" className="rounded-full bg-white border border-emerald-200 px-3 py-1.5 text-xs font-[Outfit] font-bold text-emerald-900 hover:border-fuchsia-400">Facebook</a>}
              {social.instagram && <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="rounded-full bg-white border border-emerald-200 px-3 py-1.5 text-xs font-[Outfit] font-bold text-emerald-900 hover:border-fuchsia-400">Instagram</a>}
              {social.linkedin_org && <a href={social.linkedin_org} target="_blank" rel="noopener noreferrer" className="rounded-full bg-white border border-emerald-200 px-3 py-1.5 text-xs font-[Outfit] font-bold text-emerald-900 hover:border-fuchsia-400">LinkedIn</a>}
            </div>
          </div>
        )}
        <Link
          to="/event/sponsors"
          data-testid="post-sponsors-link"
          className="block rounded-2xl border border-emerald-100 bg-white hover:border-fuchsia-300 px-4 py-3 flex items-center gap-3"
        >
          <span className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center"><Users size={16} /></span>
          <div className="flex-1"><div className="font-[Cormorant_Garamond] text-lg leading-tight">View sponsors and partners</div></div>
          <ArrowUpRight size={16} className="text-emerald-950/40" />
        </Link>
      </div>
    </EventLayout>
  );
}

function ActionRow({ icon, label, hint, href, highlight, testid }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className={`block rounded-2xl border-2 px-4 py-3 flex items-center gap-3 transition-colors ${highlight ? "border-fuchsia-300 bg-fuchsia-50/40 hover:border-fuchsia-500" : "border-emerald-100 bg-white hover:border-emerald-300"}`}
    >
      <span className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${highlight ? "bg-fuchsia-100 text-fuchsia-700" : "bg-emerald-100 text-emerald-800"}`}>{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="font-[Cormorant_Garamond] text-lg leading-tight text-emerald-950">{label}</div>
        {hint && <div className="text-[11px] font-[Outfit] text-emerald-950/60 truncate">{hint}</div>}
      </div>
      <ArrowUpRight size={16} className="text-emerald-950/40" />
    </a>
  );
}

function iconFor(id) {
  if (!id) return <Rocket size={18} />;
  if (id.includes("volunteer")) return <Users size={18} />;
  if (id.includes("5k")) return <Rocket size={18} />;
  if (id.includes("ai")) return <Star size={18} />;
  if (id.includes("early")) return <Award size={18} />;
  if (id.includes("google")) return <Star size={18} />;
  return <Rocket size={18} />;
}


/** Donation quick-pick chips · deep-links each amount into PayPal via
 * `&amount=X` query param. Falls through to a plain donate CTA if the hosted
 * button ignores the param. */
function DonationQuickPick({ baseUrl, amounts, impact }) {
  const buildUrl = (amt) => {
    try {
      const u = new URL(baseUrl);
      u.searchParams.set("amount", String(amt));
      return u.toString();
    } catch {
      const sep = baseUrl.includes("?") ? "&" : "?";
      return `${baseUrl}${sep}amount=${amt}`;
    }
  };
  return (
    <div
      data-testid="post-donate-quickpick"
      className="rounded-2xl border-2 border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 to-white p-4"
    >
      <div className="flex items-start gap-3 mb-3">
        <span className="h-10 w-10 rounded-xl bg-fuchsia-600 text-white flex items-center justify-center shrink-0">
          <Heart size={18} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-[Cormorant_Garamond] text-xl leading-tight text-emerald-950">
            Support the mission
          </div>
          {impact && (
            <div className="text-[11px] font-[Outfit] text-emerald-950/60 mt-0.5 leading-snug">
              {impact.length > 90 ? `${impact.slice(0, 90)}…` : impact}
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {amounts.slice(0, 3).map((amt) => (
          <a
            key={amt}
            href={buildUrl(amt)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`post-donate-amt-${amt}`}
            className="rounded-xl bg-white border-2 border-fuchsia-200 hover:border-fuchsia-500 hover:bg-fuchsia-600 hover:text-white transition-colors text-center py-2.5 font-[Outfit] font-bold text-emerald-950"
          >
            ${amt}
          </a>
        ))}
      </div>
      {amounts.length > 3 && (
        <div className="grid grid-cols-2 gap-2 mt-2">
          {amounts.slice(3, 5).map((amt) => (
            <a
              key={amt}
              href={buildUrl(amt)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`post-donate-amt-${amt}`}
              className="rounded-xl bg-white border-2 border-fuchsia-200 hover:border-fuchsia-500 hover:bg-fuchsia-600 hover:text-white transition-colors text-center py-2.5 font-[Outfit] font-bold text-emerald-950"
            >
              ${amt}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
